import { Icon, IconName } from '@openeducation/oe-rn-components';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, View, Animated, Easing } from 'react-native';

import KeyboardIndicator from './keyboard-indicator';
import styles from './keyboard-legend.style';
import type { TemplateType } from '../../enums/template-type';
import { useColors } from '../../hooks';
import { useStoredCounter } from '../../hooks/use-stored-counter';
import ThemedText2 from '../themed-text-2';
import { ThemedView } from '../themed-view';

interface KeyboardLegendProps {
  templateType: TemplateType;
  text: string;
  indicatorList?: string[];
  iconIndicators?: IconName[];
  forceHide?: boolean;
}

const DISPLAY_LIMIT = 4;

export default function KeyboardLegend({
  templateType,
  text,
  indicatorList = [],
  iconIndicators = [],
  forceHide = false,
}: KeyboardLegendProps) {
  const colors = useColors();
  const [hidden, setHidden] = useState(false);
  const isVisible = useRef(false);
  const shouldCheckCounter = useRef(true);
  const animation = useRef(new Animated.Value(0));
  const { getCounter } = useStoredCounter();

  useEffect(() => {
    if (hidden || Platform.OS !== 'web') {
      return;
    }

    let timeoutUpId: NodeJS.Timeout;
    let timeoutDownId: NodeJS.Timeout;

    async function showHide() {
      if (shouldCheckCounter.current) {
        shouldCheckCounter.current = false;

        const counter = await getCounter(templateType);
        if (counter > DISPLAY_LIMIT) {
          return;
        }
      }

      if (!isVisible.current) {
        timeoutUpId = setTimeout(() => {
          isVisible.current = true;
          Animated.timing(animation.current, {
            toValue: 1,
            delay: 0,
            duration: 1000,
            useNativeDriver: true,
            easing: Easing.in(Easing.elastic(1.1)),
          }).start();
        }, 1000);
      }

      timeoutDownId = setTimeout(
        () => {
          Animated.timing(animation.current, {
            toValue: 2,
            delay: 0,
            duration: isVisible.current ? 1000 : 0,
            useNativeDriver: true,
            easing: Easing.out(Easing.elastic(1.1)),
          }).start(() => setHidden(true));
        },
        forceHide ? 0 : 5000
      );
    }

    showHide();

    return () => {
      clearTimeout(timeoutDownId);
      clearTimeout(timeoutUpId);
    };
  }, [forceHide, hidden, templateType]);

  const bottomAnimation = animation.current.interpolate({
    inputRange: [0, 1, 2],
    outputRange: [-100, 0, -100],
    extrapolate: 'clamp',
  });

  if (Platform.OS !== 'web') {
    return null;
  }

  return (
    <Animated.View
      style={[styles.wrapper, { bottom: bottomAnimation }]}
      pointerEvents="none"
    >
      <ThemedView
        type="background"
        style={[styles.container, { borderColor: colors.borderColor }]}
      >
        <ThemedText2 style={styles.text}>{text}</ThemedText2>
        <View style={styles.indicatorContainer}>
          {indicatorList?.map((indicator) => (
            <KeyboardIndicator key={indicator}>
              <ThemedText2 fontWeightStyle="bold">{indicator}</ThemedText2>
            </KeyboardIndicator>
          ))}
          {iconIndicators?.map((icon) => (
            <KeyboardIndicator key={icon}>
              <Icon name={icon} size={16} type="solid" />
            </KeyboardIndicator>
          ))}
        </View>
      </ThemedView>
    </Animated.View>
  );
}

export function KeyboardLegendPadding() {
  if (Platform.OS !== 'web') {
    return null;
  }

  return <View style={styles.padding} />;
}
